import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { ProgressBar } from './ProgressBar';
import { useAuth } from './AuthContext';
import { useTranslation } from '../i18n/TranslationContext';

interface AuthContainerProps {
  children: React.ReactNode;
  showBack?: boolean;
  showProgress?: boolean;
  onBack?: () => void;
  title?: string;
}

export function AuthContainer({ 
  children, 
  showBack = false, 
  showProgress = false, 
  onBack,
  title 
}: AuthContainerProps) {
  const { currentStep } = useAuth();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-ucm-bg-gray flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] p-6 md:p-10 relative">
        {/* Header with back button and progress */}
        {(showBack || showProgress) && (
          <div className="flex items-center justify-between mb-8">
            {showBack ? (
              <button
                onClick={onBack}
                className="w-12 h-12 rounded-full bg-ucm-border-light flex items-center justify-center hover:bg-ucm-border-medium transition-colors"
              >
                <ArrowLeft size={20} className="text-ucm-text-medium" />
              </button>
            ) : (
              <div className="w-12 h-12" />
            )}
            
            {showProgress && (
              <div className="flex-1 ml-4">
                <ProgressBar currentStep={currentStep} totalSteps={6} />
              </div>
            )}
          </div>
        )}

        {/* UCM Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-ucm-primary-teal rounded-2xl text-white font-bold text-xl">
            UCM
          </div>
        </div>

        {/* Content */}
        {children}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-ucm-text-light">
            {t('authContainer.termsConsent')}{' '}
            <a 
              href="https://ucm.agency/en/terms-and-conditions/" 
              target="_blank"
              rel="noopener noreferrer"
              className="terms-link"
            >
              {t('authContainer.termsAndConditions')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}